import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  DataTable,
  EmptyState,
  Icon,
  Panel,
  Tag,
  useErrorDialog,
  type Column,
} from '@dayon/design-system';
import {
  RESIDENCY_COUNTRIES_KEY,
  RESIDENCY_MODE_EFFECT,
  RESIDENCY_MODE_KEY,
  type ResidencyPolicyResult,
  type ResidencyPolicyRow,
} from '@dayon/contracts';

import { api, ApiRequestError } from '../../../lib/api.ts';
import { useSession } from '../../../lib/session.tsx';
import { ResidenceCounts } from './ResidenceCounts.tsx';
import { ResidencyPolicyDialog } from './ResidencyPolicyDialog.tsx';

/**
 * RESIDENCY — a section of Platform settings rather than a screen of its own.
 *
 * WHY HERE. The policy IS two `platform.setting` rows,
 * `player.residency_mode` and `player.residency_countries`; it is per brand
 * with a platform-wide fallback, exactly like every other overridable
 * parameter; it is read for `platform.view` and written for `platform.manage`,
 * which is this screen's gate; and the write goes through the same path as
 * every other parameter, so the change lands in the configuration log LOWER
 * DOWN THIS SAME PAGE. A separate module would have put the change and the log
 * that makes it accountable on two screens, and would have duplicated the
 * parameters table's read, write and refusal plumbing to show two of its rows
 * differently.
 *
 * WHY IT IS NOT JUST TWO ROWS IN THAT TABLE. Because as two rows it is
 * unreadable and unsafe. `BLOCK_LIST` in one row and `PH` in another are two
 * untyped strings with nothing on screen saying they are one decision, nothing
 * saying which brands follow which, nothing stating that the shipped default
 * enforces nothing at all — and, until `sql/330`, nothing stopping the two
 * being saved in states that contradict each other. This section is the same
 * two rows, shown as the decision they are.
 *
 * Brands & licences is the other screen this could have lived on. It is not
 * there because a residency policy is not a property of the licence RECORD —
 * `platform.brand` holds a reference and an expiry and no regime — and putting
 * it there would have implied the platform knows which licence the operator
 * holds. It does not, and this section says so.
 */

/** The two keys, printed so an operator can find them in the table below. */
const KEYS = `${RESIDENCY_MODE_KEY} · ${RESIDENCY_COUNTRIES_KEY}`;

function modeTone(row: ResidencyPolicyRow) {
  return RESIDENCY_MODE_EFFECT[row.mode].enforces ? 'accent' : 'quiet';
}

function policyColumns(
  onEdit: (row: ResidencyPolicyRow) => void,
  mayManage: boolean,
): Column<ResidencyPolicyRow>[] {
  return [
    {
      key: 'scope',
      header: 'Applies to',
      render: (r) => (
        <>
          {r.brandName ?? 'Every brand — the platform-wide policy'}
          {r.brandId === null ? (
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              followed by every brand without a policy of its own
            </div>
          ) : r.modeInherited && r.countriesInherited ? (
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              inherited — this brand has no policy of its own
            </div>
          ) : r.modeInherited || r.countriesInherited ? (
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              {/* Half-inherited is a real and confusing state: a brand can
                  override the mode and still follow the platform-wide list, or
                  the reverse. Saying only "override" would hide which half. */}
              part inherited — {r.modeInherited ? 'the list' : 'the mode'} is this brand&rsquo;s,{' '}
              {r.modeInherited ? 'the mode' : 'the list'} is the platform-wide one
            </div>
          ) : (
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              this brand&rsquo;s own policy
            </div>
          )}
        </>
      ),
    },
    {
      key: 'mode',
      header: 'Mode',
      render: (r) => (
        <>
          <Tag tone={modeTone(r)}>{RESIDENCY_MODE_EFFECT[r.mode].label}</Tag>
          {!RESIDENCY_MODE_EFFECT[r.mode].enforces ? (
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              nothing is enforced
            </div>
          ) : null}
        </>
      ),
    },
    {
      key: 'countries',
      header: 'Countries',
      render: (r) =>
        !RESIDENCY_MODE_EFFECT[r.mode].enforces ? (
          /* Not blank, and not the list. Under ACCEPT_ALL the list is genuinely
             not read, and printing it would show a restriction that is not in
             force. */
          <span className="text-subtle">
            not read under this mode
            {r.countries.length > 0 ? ` — ${r.countries.length} kept` : ''}
          </span>
        ) : (
          <span className="tabular">{r.countries.join(', ')}</span>
        ),
    },
    {
      key: 'action',
      header: 'Action',
      numeric: true,
      render: (r) => (
        <Button
          small
          disabled={!mayManage}
          aria-label={
            mayManage
              ? `Set the residency policy for ${r.brandName ?? 'every brand'}`
              : `Set the residency policy for ${r.brandName ?? 'every brand'} — you do not hold platform.manage, which changing a parameter requires`
          }
          onClick={() => onEdit(r)}
        >
          Set policy
        </Button>
      ),
    },
  ];
}

export function ResidencyPolicy() {
  const { brandId, brands, allowed } = useSession();
  const { showError } = useErrorDialog();

  const [policy, setPolicy] = useState<ResidencyPolicyResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState<ResidencyPolicyRow | null>(null);
  /* Bumped after an accepted write so the policy AND the counts re-read: a mode
     change moves the "would be refused today" tile, and a stale tile beside a
     changed policy is the one number an operator would act on. */
  const [writeCount, setWriteCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<ResidencyPolicyResult>('/api/residency/policy')
      .then((p) => {
        if (cancelled) return;
        setPolicy(p);
        setFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setPolicy(null);
        setFailed(true);
        showError({
          title: 'Could not load the residency policy',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [showError, writeCount]);

  const brandLabel = useMemo(
    () => brands.find((b) => b.id === brandId)?.name ?? 'every brand in your scope',
    [brands, brandId],
  );

  /* The platform-wide row stays visible under a brand scope because every brand
     without an override follows it — hiding it would tell an operator scoped to
     one brand that the brand has no policy, when in fact it has the global one. */
  const rows = useMemo(() => {
    if (!policy) return [];
    const scoped = brandId ? policy.brands.filter((b) => b.brandId === brandId) : policy.brands;
    return [policy.platformWide, ...scoped];
  }, [policy, brandId]);

  const enforcing = policy
    ? [policy.platformWide, ...policy.brands].filter(
        (r) => RESIDENCY_MODE_EFFECT[r.mode].enforces,
      )
    : [];

  /*
   * HOW WIDE THE "nothing is enforced" CLAIM MAY BE.
   *
   * `GET /api/residency/policy` returns only the brands inside the reader's
   * access scope, so a brand-restricted administrator is being shown a subset.
   * "No residency restriction is in force anywhere on this platform" would then
   * be a statement about brands they cannot see, and it might be false — the
   * exact shape of overclaim this console exists to avoid.
   *
   * THIS WAS COMPUTED HERE AND COULD NOT WORK. It compared the length of the
   * returned `brands` against `session.brands` — but BOTH are filtered by the
   * same `brandScopeFilter`, so the two are always equal, the flag was always
   * true, and the caveat below it was dead code. A restricted reader was being
   * told the platform-wide sentence regardless.
   *
   * The server answers it now, from the only thing that knows: whether the
   * scope filter is null.
   */
  const seesEveryBrand = policy?.coversEveryBrand === true;

  return (
    <Panel
      title="Player residency"
      actions={
        <span className="text-subtle" style={{ fontSize: '11px' }}>
          {KEYS}
        </span>
      }
    >
      {/*
        THE POSTURE, STATED FIRST AND IN THE OPERATOR'S OWN TERMS.

        "We have a residency control" and "the residency control is switched
        off" are different facts, and only one of them is true today. This
        notice is the one that is.
      */}
      {loading && !policy ? (
        <div className="skeleton" style={{ height: '120px' }}>
          &nbsp;
        </div>
      ) : failed || !policy ? (
        <EmptyState icon="warning-circle" title="The residency policy could not be read">
          {/* THIS SENTENCE USED TO OPEN WITH THE ROUTE IT WAITED ON. The path is
              recorded in the scope note higher up this file, which is where anyone
              debugging a wrong empty state will look; an operator cannot call a
              route, and the address told them nothing this sentence does not tell
              them better. What they must not get wrong is WHICH of the two states
              they are in, and that line is drawn as hard here as it was before:
              acting on "no residency policy is set" when the truth is "nobody
              could ask" is the expensive mistake on a control that decides who may
              open an account at all. */}
          The request for the policy did not come back, so no policy is shown here — not because no
          policy is set. This screen must never read as &ldquo;nothing is restricted&rdquo; when the
          truth is that nobody could ask.
        </EmptyState>
      ) : (
        <>
          {enforcing.length === 0 ? (
            <div className="notice">
              <Icon name="warning-circle" />
              <div>
                <strong>
                  {seesEveryBrand
                    ? 'No residency restriction is in force anywhere on this platform.'
                    : 'No residency restriction is in force on any brand you can see.'}
                </strong>{' '}
                {seesEveryBrand ? 'Every brand' : 'Each of them'} accepts a registration from a
                resident of any country, which is what <strong>Accept every country</strong> means
                and what the platform ships with.
                {seesEveryBrand ? null : (
                  <>
                    {' '}
                    Brands outside your access scope are not listed here and this says nothing
                    about them.
                  </>
                )}
                <br />
                PAGCOR&rsquo;s offshore regime permits serving players outside the Philippines and{' '}
                <strong>prohibits Philippine residents</strong>; its domestic PIGO regime is the
                reverse. Under an offshore licence this setting would have to block PH. Nothing in
                this platform records which licence the operator holds &mdash; there is no regime
                on the brand record, only a reference and an expiry
                &mdash; so this console states what is switched on and does not guess at what
                should be.
              </div>
            </div>
          ) : (
            <div className="notice notice-quiet">
              <Icon name="info" />
              <div>
                {enforcing.length === 1
                  ? 'One scope enforces a residency restriction'
                  : `${enforcing.length} scopes enforce a residency restriction`}
                . It is checked at <strong>registration only</strong>: a player already on the
                roster is never re-checked, never closed and never flagged by a policy here, and no
                job anywhere in this platform revisits one. Existing accounts a policy would
                exclude are counted below.
              </div>
            </div>
          )}

          <DataTable
            caption="Residency policy per brand, with the platform-wide policy every brand without one follows"
            columns={policyColumns(setEditing, allowed('platform.manage'))}
            rows={rows}
            rowKey={(r) => r.brandId ?? 'platform'}
            empty={
              <EmptyState icon="globe-hemisphere-east" title="No brand in your scope">
                There is no brand you may act on, so there is no per-brand policy to show. The
                platform-wide policy above still applies to every brand.
              </EmptyState>
            }
          />

          {!allowed('platform.manage') ? (
            <div className="notice notice-quiet">
              <Icon name="info" />
              <div>
                You can read the policy and the log of who changed it —{' '}
                <span className="signin-perm">platform.view</span> — and you cannot change it.
                Setting a residency policy requires{' '}
                <span className="signin-perm">platform.manage</span>, the same capability every
                other parameter write on this screen needs: this one decides who may open an
                account at all.
              </div>
            </div>
          ) : null}

          {/* The breakdown counts PLAYERS, and is gated on the capability that
              matches what it discloses rather than on the one that opened this
              screen. The PAGCOR Regulator holds platform.view and not
              players.view: it reads the whole policy, and is told why the
              breakdown is not shown rather than left with a panel that
              silently fails to load. */}
          {allowed('players.view') ? (
            <>
              <div className="panel-title">Where players say they live</div>
              <ResidenceCounts brandId={brandId} brandLabel={brandLabel} reloadKey={writeCount} />
            </>
          ) : (
            <div className="notice notice-quiet">
              <Icon name="info" />
              <div>
                The breakdown of where players live is not shown: it counts the roster, which needs{' '}
                <span className="signin-perm">players.view</span>, and your role does not hold it.
                The policy above is configuration and you can read all of it.
              </div>
            </div>
          )}
        </>
      )}

      <ResidencyPolicyDialog
        target={editing}
        countries={policy?.countries ?? []}
        onClose={() => setEditing(null)}
        onDone={() => setWriteCount((n) => n + 1)}
      />
    </Panel>
  );
}
